import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { CornerDownLeft, Cpu, FileText, FolderTree, type LucideIcon, MapPin, Route, Search, Truck, UserRound } from 'lucide-react';
import { Dialog as D } from 'radix-ui';
import { type KeyboardEvent, useMemo, useState } from 'react';
import { api, isApiError, unwrap } from '@/lib/api/client';
import type { SearchHit } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import { useDebouncedValue } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { isSafeAppPath } from '@/lib/search';
import { Kbd } from '@/ui';
import { useNav } from './nav';
import s from './SearchPalette.module.css';
import { closeSearch, useSearchState } from './searchStore';

const TYPE_ICONS: Record<string, LucideIcon> = { vehicle: Truck, driver: UserRound, device: Cpu, group: FolderTree, place: MapPin, trip: Route };

interface Result {
  id: string;
  type: string;
  label: string;
  sub?: string | null;
  group: string;
  href: string;
}

function searchEntities(q: string): Promise<SearchHit[]> {
  return unwrap(api.GET('/api/search', { params: { query: { q, limit: 12 } } }));
}

/** Ctrl K: find any page, vehicle, driver or device (IA §1.4). */
export function SearchPalette() {
  const { open, query } = useSearchState();
  return (
    <D.Root open={open} onOpenChange={(o) => !o && closeSearch()}>
      <D.Portal>
        <D.Overlay className={s.overlay} />
        <D.Content className={s.palette} aria-describedby={undefined}>
          <D.Title className="sr-only">{t('Search')}</D.Title>
          {/* Mounted per opening, so each search starts from the requested query. */}
          <PaletteBody initial={query} />
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

function PaletteBody({ initial }: { initial: string }) {
  const [q, setQ] = useState(initial);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const { all } = useNav();
  const debounced = useDebouncedValue(q.trim(), 200);

  const entities = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => searchEntities(debounced),
    enabled: debounced.length >= 2,
    retry: false,
    staleTime: 30_000,
  });
  const unavailable = isApiError(entities.error, 404) || isApiError(entities.error, 405);

  const results = useMemo<Result[]>(() => {
    const needle = debounced.toLowerCase();
    const pages: Result[] = all.flatMap((item) => [
      { id: `page:${item.key}`, type: 'page', label: t(item.label), sub: item.description ? t(item.description) : null, group: t('Pages'), href: item.to },
      ...item.children.map((c) => ({ id: `page:${item.key}:${c.key}`, type: 'page', label: t(c.label), sub: t(item.label), group: t('Pages'), href: c.to })),
    ]);
    const matchedPages = needle ? pages.filter((p) => `${p.label} ${p.sub ?? ''}`.toLowerCase().includes(needle)) : pages.slice(0, 8);
    const hits: Result[] = (entities.data ?? [])
      .filter((h) => isSafeAppPath(h.href))
      .map((h) => ({ id: `${h.type}:${h.id}`, type: h.type, label: h.label, sub: h.sub, group: h.type_label, href: h.href }));
    return [...hits, ...matchedPages];
  }, [all, debounced, entities.data]);

  const go = (r: Result | undefined) => {
    if (!r) return;
    closeSearch();
    void navigate({ to: r.href as '/' });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, Math.min(results.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1))));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[active]);
    }
  };

  const headers = results.map((r, i) => (r.group !== results[i - 1]?.group ? r.group : null));
  return (
    <>
      <div className={s.inputRow}>
        <Search className={s.icon} aria-hidden />
        <input
          autoFocus
          className={s.input}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={t('Search pages, vehicles, drivers, devices…')}
          role="combobox"
          aria-expanded
          aria-controls="search-results"
          aria-activedescendant={results[active] ? `sr-${active}` : undefined}
        />
        <Kbd>Esc</Kbd>
      </div>
      <div id="search-results" role="listbox" className={s.results}>
        {results.length === 0 && <p className={s.empty}>{entities.isFetching ? t('Searching…') : t('No matches')}</p>}
        {results.map((r, i) => {
          const Icon = TYPE_ICONS[r.type] ?? FileText;
          return (
            <div key={r.id}>
              {headers[i] && <div className={s.group}>{headers[i]}</div>}
              <div
                id={`sr-${i}`}
                role="option"
                aria-selected={i === active}
                className={cx(s.result, i === active && s.active)}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(r)}
              >
                <Icon className={s.resultIcon} aria-hidden />
                <span className={s.resultText}>
                  <span className={s.resultLabel}>{r.label}</span>
                  {r.sub && <span className={s.resultSub}>{r.sub}</span>}
                </span>
                {i === active && <CornerDownLeft className={s.enter} aria-hidden />}
              </div>
            </div>
          );
        })}
      </div>
      {entities.error && debounced.length >= 2 && (
        <p className={s.note}>
          {unavailable
            ? t('Searching vehicles, drivers and devices is not available on this server yet; pages are searched.')
            : t('Searching vehicles, drivers and devices failed; pages are searched.')}
        </p>
      )}
    </>
  );
}
